/**
 * waap-websites-tab.tsx — Read-only Websites list tab for a WAAP service.
 *
 * Lists the account's WAAP websites (vendor publicWebsiteDTO via the PUBLIC
 * Open-API). Each row shows domain, HTTP/2, redirect, origin count, and
 * creation date. Read-only in B1 — mutations (delete, reset-security) land in B4.
 *
 * Data source:
 *   GET /waap/domains/:anchorId/websites  (useActAsWAAPWebsites)
 */

import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useActAsWAAPWebsites, type ActAsWAAPWebsite } from '@/api/act-as-waap';
import { formatDate } from '@/lib/format';

/* ── Column definitions ──────────────────────────────────────────────────── */

const columns: ColumnDef<ActAsWAAPWebsite, unknown>[] = [
  {
    id: 'domain',
    accessorKey: 'domain',
    header: 'Domain',
    cell: ({ row }) => (
      <span style={{ fontWeight: 500, color: 'var(--vn-fg-default)', wordBreak: 'break-all' }}>
        {row.original.domain}
      </span>
    ),
  },
  {
    id: 'origins',
    header: 'Origins',
    cell: ({ row }) => {
      const origins = row.original.origins ?? [];
      if (origins.length === 0) return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
      return (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          {origins.slice(0, 2).map((o) => (
            <span key={o} style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {o}
            </span>
          ))}
          {origins.length > 2 && (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
              +{origins.length - 2} more
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'http2',
    header: 'HTTP/2',
    cell: ({ row }) => (
      <Badge tone={row.original.http2 ? 'success' : 'neutral'}>
        {row.original.http2 ? 'On' : 'Off'}
      </Badge>
    ),
  },
  {
    id: 'redirect',
    header: 'HTTPS Redirect',
    cell: ({ row }) => (
      <Badge tone={row.original.redirect_http_to_https ? 'success' : 'neutral'}>
        {row.original.redirect_http_to_https ? 'On' : 'Off'}
      </Badge>
    ),
  },
  {
    id: 'alternate_domains',
    header: 'Alternate Domains',
    cell: ({ row }) => {
      const alts = row.original.alternate_domain_names ?? [];
      if (alts.length === 0) return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
      return (
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {alts.length} domain{alts.length !== 1 ? 's' : ''}
        </span>
      );
    },
  },
  {
    id: 'vendor_id',
    header: 'Vendor ID',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
        {row.original.id}
      </span>
    ),
  },
  {
    id: 'created',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
        {formatDate(row.original.created)}
      </span>
    ),
  },
];

/* ── Component ───────────────────────────────────────────────────────────── */

interface WaapWebsitesTabProps {
  anchorId: string;
}

/**
 * Websites tab — WAAP website list for the target org's WAAP service (read-only).
 * Lists all websites on the account via the PUBLIC Open-API (anchor domain session).
 * Mutations (delete, reset-security) are B4 scope.
 */
export function WaapWebsitesTab({ anchorId }: WaapWebsitesTabProps) {
  const { t } = useTranslation();
  const query = useActAsWAAPWebsites(anchorId);

  if (query.isError) {
    return (
      <Card>
        <CardHeader><CardTitle>{t('waap_domain.websites_title', 'Websites')}</CardTitle></CardHeader>
        <CardBody>
          <Alert
            tone="danger"
            title={t('waap_domain.websites_load_error', 'Failed to load websites')}
            actions={
              <Button size="sm" variant="ghost" onClick={() => query.refetch()}>
                {t('common.retry', 'Retry')}
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>{t('waap_domain.websites_title', 'Websites')}</CardTitle></CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  const rows = query.data ?? [];

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching
              ? t('waap_domain.refreshing', 'Refreshing…')
              : t('waap_domain.refresh', 'Refresh')}
          </Button>
        }
      >
        <CardTitle>{t('waap_domain.websites_title', 'Websites')}</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          dense
          emptyState={
            <EmptyState
              icon={<Globe size={24} />}
              title={t('waap_domain.websites_empty', 'No WAAP websites')}
              description={t(
                'waap_domain.websites_empty_desc',
                'No websites found for this WAAP service in the target org.',
              )}
            />
          }
          footer={
            rows.length > 0
              ? `${rows.length} website${rows.length !== 1 ? 's' : ''}`
              : undefined
          }
        />
      </CardBody>
    </Card>
  );
}
